// @ts-nocheck
// Create-from-PDF full-screen overlay (PRD Phase 3): choose -> upload ->
// processing -> review -> link -> commit, then hands the new claim id back to
// the parent (which navigates into the editor). Built from the claims `shared`
// primitives; escape/focus safety mirrors shared/Modal.js (refs keyed on `open`).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDropzone } from 'react-dropzone';

import { listBillingProviders } from '../../api/claimsClient';
import { commitClaimImport, uploadClaimImport } from '../../api/claimImportApi';
import { useClaimImportPolling } from '../../hooks/useClaimImportPolling';
import { Btn, Chip, Field, Icon, Input, PatientPicker, Section } from './shared';
import humanizeFieldLabel from './shared/humanizeFieldLabel';
import EOBReview from './createFromPdf/EOBReview';
import EOBAttach from './createFromPdf/EOBAttach';
import FlowSteps from './createFromPdf/FlowSteps';

const LOW_CONFIDENCE = 0.75;

// Entity blocks rendered as editable scalar field groups (arrays like diagnoses
// / service_lines pass through untouched on commit).
const SCALAR_ENTITIES = [
  ['claim', 'Claim'],
  ['insured', 'Insured / Subscriber'],
  ['patient', 'Patient'],
  ['payer', 'Payer'],
  ['billing_provider', 'Billing Provider'],
  ['service_facility', 'Service Facility'],
];

// ProcessingStage: 4 labelled steps driven by real poll status (no setTimeout).
const PROC_STEPS = [
  { label: 'Uploading document', sub: 'Securely transferring PDF to the worker' },
  { label: 'Running OCR', sub: 'easyocr + PyMuPDF reading boxes and tables' },
  { label: 'Structured extraction', sub: 'Gemini 2.5 mapping fields → CMS-1500 schema' },
  { label: 'Validating & scoring', sub: 'Per-field confidence and required-field check' },
];

// Map real poll status to a 0-based step index (decorative chrome only).
function statusToStep(status) {
  if (!status || status === 'pending' || status === 'queued') return 0;
  if (status === 'uploading') return 1;
  if (status === 'validating' || status === 'scoring') return 3;
  // Any other non-terminal in-flight status (running, processing, extracting…)
  return 2;
}

function leafValue(leaf) {
  return leaf && typeof leaf === 'object' ? (leaf.value ?? '') : (leaf ?? '');
}
function leafFound(leaf) {
  if (leaf && typeof leaf === 'object') return leaf.found !== false && (leaf.value ?? '') !== '';
  return (leaf ?? '') !== '';
}
function leafConfidence(leaf) {
  return leaf && typeof leaf === 'object' && typeof leaf.confidence === 'number' ? leaf.confidence : 1;
}

/** Null-render adapter: runs the poll hook and reports terminal status + raw
 *  status ticks up via onStatus so the processing UI can track real steps. */
function ClaimImportPollingHook({ extractionId, restartKey, onTerminal, onStatus }) {
  const { status, data, error } = useClaimImportPolling(extractionId, restartKey || 0);
  useEffect(() => {
    if (onStatus) onStatus(status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);
  useEffect(() => {
    if (status === 'success' || status === 'needs_review' || status === 'failed') {
      onTerminal(status, data, error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);
  return null;
}

export default function CreateFromPdfOverlay({ open, onClose, onCommit }) {
  const [stage, setStage] = useState('choose');
  const [docKind, setDocKind] = useState('cms1500');
  const [extractionId, setExtractionId] = useState(null);
  const [restartKey, setRestartKey] = useState(0);
  const [confirmed, setConfirmed] = useState({});
  const [blockingMissing, setBlockingMissing] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [patientLink, setPatientLink] = useState(null);
  const [patientSnapshot, setPatientSnapshot] = useState(null);
  const [providerLink, setProviderLink] = useState({ kind: 'new' });
  const [providers, setProviders] = useState([]);
  const [committing, setCommitting] = useState(false);
  // Real poll status forwarded from ClaimImportPollingHook for ProcessingStage.
  const [pollStatus, setPollStatus] = useState(null);
  // EOB attach-vs-create branch (Phase 6): default attach; null target until picked.
  const [attachMode, setAttachMode] = useState('attach');
  const [attachClaim, setAttachClaim] = useState(null);

  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const pollCancelRef = useRef(null);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Reset to a clean state whenever the overlay (re)opens.
  useEffect(() => {
    if (!open) return undefined;
    setStage('choose');
    setDocKind('cms1500');
    setExtractionId(null);
    setConfirmed({});
    setBlockingMissing([]);
    setError(null);
    setPatientLink(null);
    setPatientSnapshot(null);
    setProviderLink({ kind: 'new' });
    setPollStatus(null);
    setAttachMode('attach');
    setAttachClaim(null);
    const onKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current?.();
    };
    window.addEventListener('keydown', onKey);
    dialogRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const handleClose = useCallback(() => {
    pollCancelRef.current?.();
    onClose?.();
  }, [onClose]);

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (!acceptedFiles.length) return;
      setUploading(true);
      setError(null);
      try {
        const res = await uploadClaimImport(acceptedFiles[0], docKind);
        setExtractionId(res.extraction_id);
        setRestartKey((k) => k + 1);
        setPollStatus(null);
        setStage('processing');
      } catch (_e) {
        setError('Upload failed. Please try again.');
      } finally {
        setUploading(false);
      }
    },
    [docKind],
  );

  const onTerminal = useCallback((status, data) => {
    if (status === 'failed') {
      setError((data && data.error_message) || 'Extraction failed.');
      setStage('upload');
      return;
    }
    const extracted = (data && data.extracted_data) || {};
    setConfirmed(JSON.parse(JSON.stringify(extracted)));
    setBlockingMissing((data && data.blocking_missing) || []);
    setStage('review');
  }, []);

  const setLeaf = useCallback((entity, field, value) => {
    setConfirmed((prev) => {
      const next = { ...prev, [entity]: { ...(prev[entity] || {}) } };
      next[entity][field] = { value, found: true, confidence: 1 };
      return next;
    });
  }, []);

  // EOB per-line place_of_service writes back into the confirmed contract at
  // adjudication_lines[i].place_of_service (a scalar leaf the commit reads).
  const setLinePlaceOfService = useCallback((index, value) => {
    setConfirmed((prev) => {
      const lines = Array.isArray(prev.adjudication_lines) ? [...prev.adjudication_lines] : [];
      if (!lines[index]) return prev;
      lines[index] = { ...lines[index], place_of_service: { value, found: true, confidence: 1 } };
      return { ...prev, adjudication_lines: lines };
    });
  }, []);

  // Required fields still unsatisfied (blocks Continue from review).
  const unsatisfiedRequired = useMemo(() => {
    return blockingMissing.filter((key) => {
      if (key === 'service_lines') {
        const lines = confirmed.service_lines || [];
        return !(Array.isArray(lines) && lines.length > 0);
      }
      // EOB per-line place_of_service: satisfied only when EVERY adjudication
      // line carries a non-blank place_of_service.
      if (key === 'adjudication_lines.place_of_service') {
        const lines = confirmed.adjudication_lines || [];
        if (!Array.isArray(lines) || lines.length === 0) return true;
        return !lines.every((line) => leafValue(line && line.place_of_service) !== '');
      }
      const [entity, field] = key.split('.');
      const leaf = confirmed[entity] && confirmed[entity][field];
      return leafValue(leaf) === '';
    });
  }, [blockingMissing, confirmed]);

  const reviewSummary = useMemo(() => {
    let low = 0;
    let notFound = 0;
    SCALAR_ENTITIES.forEach(([entity]) => {
      const block = confirmed[entity];
      if (!block || typeof block !== 'object') return;
      Object.values(block).forEach((leaf) => {
        if (!leafFound(leaf)) notFound += 1;
        else if (leafConfidence(leaf) < LOW_CONFIDENCE) low += 1;
      });
    });
    return { low, notFound, required: unsatisfiedRequired.length };
  }, [confirmed, unsatisfiedRequired]);

  const goLink = useCallback(async () => {
    setStage('link');
    try {
      const list = await listBillingProviders();
      setProviders(list || []);
    } catch (_e) {
      setProviders([]);
    }
  }, []);

  // EOB review -> attach-vs-create choice; CMS-1500 review -> link directly.
  const goAfterReview = useCallback(() => {
    if (docKind === 'eob') {
      setStage('attach');
      return;
    }
    goLink();
  }, [docKind, goLink]);

  const doAttachCommit = useCallback(async () => {
    if (!attachClaim) return;
    setCommitting(true);
    setError(null);
    try {
      const res = await commitClaimImport(extractionId, {
        mode: 'attach',
        target_claim_id: attachClaim.id,
        confirmed,
      });
      onCommit?.(res.claim_id);
    } catch (_e) {
      setError('Attach failed. Please pick a claim and try again.');
    } finally {
      setCommitting(false);
    }
  }, [attachClaim, confirmed, extractionId, onCommit]);

  const doCommit = useCallback(async () => {
    if (!patientLink) return;
    setCommitting(true);
    setError(null);
    try {
      const res = await commitClaimImport(extractionId, {
        confirmed,
        patient_link: patientLink,
        provider_link: providerLink,
      });
      onCommit?.(res.claim_id);
    } catch (_e) {
      setError('Commit failed. Please review the fields and try again.');
    } finally {
      setCommitting(false);
    }
  }, [confirmed, patientLink, providerLink, extractionId, onCommit]);

  if (!open) return null;

  return createPortal(
    <div
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        zIndex: 1300,
        display: 'flex',
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Create claim from PDF"
        tabIndex={-1}
        style={{
          position: 'fixed',
          inset: 24,
          background: 'var(--ab-bg)',
          borderRadius: 'var(--ab-radius-lg)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          outline: 'none',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: 'var(--ab-hairline)',
            gap: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, flexShrink: 0 }}>
            <Icon name="file-up" size={18} />
            Create claim from PDF
          </div>
          <FlowSteps stage={stage} docKind={docKind} />
          <Btn kind="ghost" size="sm" icon="x" onClick={handleClose}>
            Close
          </Btn>
        </header>

        <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
          {error && (
            <div role="alert" style={{ color: 'var(--ab-denied)', marginBottom: 12 }}>
              {error}
            </div>
          )}

          {stage === 'choose' && (
            <ChooseDocType
              docKind={docKind}
              onPick={(k) => {
                setDocKind(k);
                setStage('upload');
              }}
            />
          )}

          {stage === 'upload' && <UploadStage uploading={uploading} onDrop={onDrop} />}

          {stage === 'processing' && <ProcessingStage pollStatus={pollStatus} />}

          {stage === 'review' && docKind === 'eob' && (
            <EOBReview
              confirmed={confirmed}
              blockingMissing={blockingMissing}
              setLeaf={setLeaf}
              setLinePlaceOfService={setLinePlaceOfService}
            />
          )}

          {stage === 'review' && docKind !== 'eob' && (
            <ReviewConfirm confirmed={confirmed} summary={reviewSummary} setLeaf={setLeaf} blockingMissing={blockingMissing} />
          )}

          {stage === 'attach' && (
            <EOBAttach
              mode={attachMode}
              selectedClaim={attachClaim}
              onModeChange={(m) => {
                setAttachMode(m);
                if (m === 'create') setAttachClaim(null);
              }}
              onSelectClaim={setAttachClaim}
            />
          )}

          {stage === 'link' && (
            <ResolveLinks
              providers={providers}
              confirmed={confirmed}
              patientLink={patientLink}
              patientSnapshot={patientSnapshot}
              providerLink={providerLink}
              onSelectPatient={(p) => {
                if (!p) {
                  setPatientLink(null);
                  setPatientSnapshot(null);
                  return;
                }
                setPatientLink({ kind: 'existing', patient_id: p.patient_id || p.id });
                setPatientSnapshot(p);
              }}
              onCreateNewPatient={() => {
                setPatientLink({ kind: 'new' });
                setPatientSnapshot(null);
              }}
              onProviderChange={setProviderLink}
            />
          )}
        </div>

        <footer
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '14px 20px',
            borderTop: 'var(--ab-hairline)',
          }}
        >
          <Btn
            kind="ghost"
            size="sm"
            icon="arrow-left"
            disabled={stage === 'choose' || stage === 'processing'}
            onClick={() => {
              if (stage === 'upload') setStage('choose');
              else if (stage === 'review') setStage('upload');
              else if (stage === 'attach') setStage('review');
              else if (stage === 'link') setStage(docKind === 'eob' ? 'attach' : 'review');
            }}
          >
            Back
          </Btn>

          {stage === 'review' && (
            <Btn kind="primary" size="sm" disabled={unsatisfiedRequired.length > 0} onClick={goAfterReview}>
              Continue
            </Btn>
          )}
          {stage === 'attach' && attachMode === 'attach' && (
            <Btn kind="primary" size="sm" disabled={!attachClaim || committing} onClick={doAttachCommit}>
              {committing ? 'Attaching…' : 'Attach to claim'}
            </Btn>
          )}
          {stage === 'attach' && attachMode === 'create' && (
            <Btn kind="primary" size="sm" onClick={goLink}>
              Continue
            </Btn>
          )}
          {stage === 'link' && (
            <Btn kind="primary" size="sm" disabled={!patientLink || committing} onClick={doCommit}>
              {committing ? 'Creating…' : 'Confirm & create draft'}
            </Btn>
          )}
        </footer>
      </div>

      {stage === 'processing' && extractionId && (
        <ClaimImportPollingHook
          extractionId={extractionId}
          restartKey={restartKey}
          onTerminal={onTerminal}
          onStatus={setPollStatus}
        />
      )}
    </div>,
    document.body,
  );
}

function ChooseDocType({ docKind, onPick }) {
  const cards = [
    {
      key: 'cms1500',
      icon: 'file-text',
      // Short label must stay 'CMS-1500' — tests locate the card via this text.
      label: 'CMS-1500',
      title: 'CMS-1500 claim form',
      desc: 'A full claim form (boxes 1–33). Extracts the complete claim — patient, payer, provider, diagnoses and service lines.',
      chipLabel: 'MVP',
      chipTone: 'submitted',
    },
    {
      key: 'eob',
      icon: 'receipt',
      // Short label must stay 'EOB' — tests locate the card via this text.
      label: 'EOB',
      title: 'Explanation of Benefits',
      desc: 'A payer EOB with adjudication and denial reasons. Create a new claim from billed lines, or attach the denial to an existing claim.',
      chipLabel: 'Phase 2',
      chipTone: 'info',
    },
  ];

  return (
    <Section icon="file-text" title="What are you uploading?">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 8 }}>
        {cards.map((c) => (
          <div
            key={c.key}
            role="button"
            tabIndex={0}
            onClick={() => onPick(c.key)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onPick(c.key); }}
            style={{
              border: `1.5px solid ${docKind === c.key ? 'var(--ab-fg)' : 'var(--ab-border-hair)'}`,
              background: 'var(--ab-bg)',
              borderRadius: 'var(--ab-radius-md)',
              padding: 20,
              cursor: 'pointer',
              transition: 'box-shadow .15s',
              boxShadow: docKind === c.key ? 'var(--ab-shadow-md)' : 'none',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 'var(--ab-radius-md)',
                  background: 'var(--ab-fg)',
                  color: 'var(--ab-fg-inverse)',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon name={c.icon} size={20} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Short label kept as standalone text node so tests can locate
                    via getByText('CMS-1500') / getByText('EOB'). */}
                <div style={{ fontWeight: 700, fontSize: 16 }}>{c.label}</div>
                <div style={{ fontSize: 12, color: 'var(--ab-fg-subtle)', marginTop: 2 }}>{c.title}</div>
              </div>
              <Chip tone={c.chipTone}>{c.chipLabel}</Chip>
              {/* Radio dot */}
              <span
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  border: '1.5px solid var(--ab-fg)',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                {docKind === c.key && (
                  <span
                    style={{
                      width: 11,
                      height: 11,
                      borderRadius: '50%',
                      background: 'var(--ab-fg)',
                    }}
                  />
                )}
              </span>
            </div>
            <p
              style={{
                margin: 0,
                fontSize: 13,
                color: 'var(--ab-fg-subtle)',
                lineHeight: 1.5,
              }}
            >
              {c.desc}
            </p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function UploadStage({ uploading, onDrop }) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    disabled: uploading,
  });
  return (
    <Section icon="upload-cloud" title="Upload the PDF">
      <div
        {...getRootProps()}
        style={{
          marginTop: 8,
          padding: 52,
          textAlign: 'center',
          border: `2px dashed ${isDragActive ? 'var(--ab-fg)' : 'var(--ab-border-soft)'}`,
          borderRadius: 'var(--ab-radius-md)',
          background: isDragActive ? 'var(--ab-bg-header)' : 'var(--ab-bg)',
          cursor: 'pointer',
          transition: 'all .15s',
        }}
      >
        <input {...getInputProps()} aria-label="Upload PDF" />
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 'var(--ab-radius-md)',
            background: 'var(--ab-bg-header)',
            display: 'grid',
            placeItems: 'center',
            margin: '0 auto 16px',
          }}
        >
          <Icon name="upload-cloud" size={28} />
        </div>
        <div style={{ fontWeight: 700, fontSize: 16 }}>
          {uploading ? 'Uploading…' : isDragActive ? 'Drop the PDF here' : 'Drop a PDF here, or click to browse'}
        </div>
        <div style={{ marginTop: 6, fontSize: 13, color: 'var(--ab-fg-subtle)' }}>
          CMS-1500 or EOB · PDF up to 20 MB · PHI handled per BAA
        </div>
      </div>
    </Section>
  );
}

function ProcessingStage({ pollStatus }) {
  const stepIdx = statusToStep(pollStatus);
  const pct = Math.min(100, Math.round(((stepIdx + 1) / PROC_STEPS.length) * 100));

  return (
    <div
      style={{
        maxWidth: 620,
        margin: '0 auto',
        paddingTop: 36,
        textAlign: 'center',
        fontFamily: 'var(--ab-font-sans)',
      }}
    >
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: 'var(--ab-radius-md)',
          background: 'var(--ab-fg)',
          color: 'var(--ab-fg-inverse)',
          display: 'grid',
          placeItems: 'center',
          margin: '0 auto 20px',
        }}
      >
        <Icon name="loader" size={28} />
      </div>
      <h2 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 4px', color: 'var(--ab-fg)' }}>
        Extracting fields…
      </h2>
      <p style={{ fontSize: 13, color: 'var(--ab-fg-subtle)', margin: '0 0 26px' }}>
        Reusing the existing upload → Pub/Sub → worker pipeline · polling job status
      </p>

      {/* Progress bar */}
      <div
        style={{
          position: 'relative',
          height: 6,
          background: 'var(--ab-bg-header)',
          borderRadius: 3,
          overflow: 'hidden',
          marginBottom: 26,
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            width: `${pct}%`,
            background: 'var(--ab-fg)',
            transition: 'width .5s ease',
          }}
        />
      </div>

      {/* Step rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'left' }}>
        {PROC_STEPS.map((s, i) => {
          const done = i < stepIdx;
          const active = i === stepIdx;
          return (
            <div
              key={s.label}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 16px',
                borderRadius: 'var(--ab-radius-md)',
                border: `1px solid ${active ? 'var(--ab-fg)' : 'var(--ab-border-hair)'}`,
                background: done ? 'var(--ab-approved-soft)' : 'var(--ab-bg)',
                opacity: i > stepIdx ? 0.5 : 1,
                transition: 'all .3s',
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  flexShrink: 0,
                  display: 'grid',
                  placeItems: 'center',
                  background: done ? 'var(--ab-approved)' : active ? 'var(--ab-fg)' : 'var(--ab-bg-header)',
                  color: done || active ? 'var(--ab-fg-inverse)' : 'var(--ab-fg-subtle)',
                }}
              >
                {done ? <Icon name="check" size={14} /> : active ? <Icon name="loader" size={14} /> : i + 1}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ab-fg)' }}>{s.label}</div>
                <div style={{ fontSize: 12, color: 'var(--ab-fg-subtle)' }}>{s.sub}</div>
              </div>
              {done && (
                <Chip tone="approved" icon="check">
                  Done
                </Chip>
              )}
              {active && (
                <span style={{ fontSize: 12, color: 'var(--ab-fg-subtle)' }}>working…</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReviewConfirmSection({ entity, label, block, blockingMissing, setLeaf }) {
  const [showHidden, setShowHidden] = useState(false);

  // Build the set of required field names for this entity (e.g. "npi" from "billing_provider.npi").
  const requiredFields = new Set(
    (blockingMissing || [])
      .filter((k) => k.startsWith(`${entity}.`))
      .map((k) => k.slice(entity.length + 1)),
  );

  const fields = Object.keys(block);

  // Partition: visible = found OR required; hidden = not-found AND not-required.
  const visibleFields = fields.filter((f) => {
    const leaf = block[f];
    return leafFound(leaf) || requiredFields.has(f);
  });
  const hiddenFields = fields.filter((f) => {
    const leaf = block[f];
    return !leafFound(leaf) && !requiredFields.has(f);
  });

  function renderField(field) {
    const leaf = block[field];
    const found = leafFound(leaf);
    const conf = leafConfidence(leaf);
    const lowConf = found && conf < LOW_CONFIDENCE;
    return (
      <Field
        key={field}
        label={humanizeFieldLabel(field)}
        caption={
          !found ? 'not found' : lowConf ? `low confidence (${Math.round(conf * 100)}%)` : undefined
        }
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Input
            value={leafValue(leaf)}
            onChange={(e) => setLeaf(entity, field, e.target.value)}
            aria-label={`${entity}.${field}`}
          />
          {!found && (
            <Chip tone="denied" icon="alert-triangle">
              missing
            </Chip>
          )}
          {lowConf && (
            <Chip tone="pending" icon="alert-triangle">
              low
            </Chip>
          )}
        </div>
      </Field>
    );
  }

  return (
    <Section key={entity} icon="square" title={label}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 8 }}>
        {visibleFields.map(renderField)}
      </div>

      {hiddenFields.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            onClick={() => setShowHidden((v) => !v)}
            style={{
              background: 'none',
              border: 'none',
              padding: '4px 0',
              cursor: 'pointer',
              fontSize: 12,
              color: 'var(--ab-fg-subtle)',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <Icon name={showHidden ? 'chevron-up' : 'chevron-down'} size={13} />
            {showHidden
              ? `Hide ${hiddenFields.length} field${hiddenFields.length !== 1 ? 's' : ''} not found on the document`
              : `Show ${hiddenFields.length} field${hiddenFields.length !== 1 ? 's' : ''} not found on the document`}
          </button>
          {showHidden && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 12,
                marginTop: 8,
                opacity: 0.6,
              }}
            >
              {hiddenFields.map(renderField)}
            </div>
          )}
        </div>
      )}
    </Section>
  );
}

function ReviewConfirm({ confirmed, summary, setLeaf, blockingMissing }) {
  return (
    <div>
      <div style={{ marginBottom: 12, color: 'var(--ab-fg-subtle)' }} data-testid="review-summary">
        {summary.low} low-confidence · {summary.notFound} not found ({summary.required} required)
      </div>
      {SCALAR_ENTITIES.map(([entity, label]) => {
        const block = confirmed[entity];
        if (!block || typeof block !== 'object' || Array.isArray(block)) return null;
        const fields = Object.keys(block);
        if (!fields.length) return null;
        return (
          <ReviewConfirmSection
            key={entity}
            entity={entity}
            label={label}
            block={block}
            blockingMissing={blockingMissing}
            setLeaf={setLeaf}
          />
        );
      })}
    </div>
  );
}

// ──────────────────────────────────────────────────────
// ResolveLinks — CMS-1500 patient + provider resolve
// ──────────────────────────────────────────────────────
function ResolveLinks({
  providers,
  confirmed,
  patientLink,
  patientSnapshot,
  providerLink,
  onSelectPatient,
  onCreateNewPatient,
  onProviderChange,
}) {
  const bp = (confirmed && confirmed.billing_provider) || {};
  const pat = (confirmed && confirmed.patient) || {};
  const ins = (confirmed && confirmed.insured) || {};

  // Extracted candidate demographics for the "create new" patient card.
  const patCandidate = {
    first_name: leafValue(pat.first_name),
    last_name: leafValue(pat.last_name),
    date_of_birth: leafValue(pat.date_of_birth),
    gender: leafValue(pat.gender),
    address_line1: leafValue(pat.address_line1),
    city: leafValue(pat.city),
    state: leafValue(pat.state),
    member_id: leafValue(ins.member_id),
  };

  // Extracted candidate data for the "create new" provider card.
  const bpCandidate = {
    name: leafValue(bp.organization_name) || leafValue(bp.last_name),
    npi: leafValue(bp.npi),
    tax_id: leafValue(bp.tax_id),
    address: leafValue(bp.address_line1),
    rendering: leafValue(bp.rendering_provider_name),
    rendering_npi: leafValue(bp.rendering_npi),
  };

  const selectedExistingProviderId =
    providerLink && providerLink.kind === 'existing' ? providerLink.profile_id : null;
  const selectedProvider = providers.find((p) => p.id === selectedExistingProviderId);

  // Show TIN hint when linking an existing provider fills a missing Box 25.
  const fillsTin =
    selectedProvider &&
    selectedProvider.tax_id &&
    !bpCandidate.tax_id;

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      {/* ── Patient resolve ── */}
      <Section icon="user" title="Resolve the patient">
        <p style={{ margin: '4px 0 16px', fontSize: 14, color: 'var(--ab-fg-subtle)' }}>
          The PDF carried the patient&apos;s details. Link to an existing record or create a new one from the extracted block.
        </p>

        {/* Existing-record card — PatientPicker is the "match found" affordance */}
        <div
          style={{
            border: `1.5px solid ${patientLink && patientLink.kind === 'existing' ? 'var(--ab-fg)' : 'var(--ab-border-hair)'}`,
            borderRadius: 'var(--ab-radius-md)',
            padding: 16,
            background: 'var(--ab-bg)',
            marginBottom: 14,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <Chip tone="approved" icon="git-merge">
              Match found
            </Chip>
            <span style={{ fontSize: 13, color: 'var(--ab-fg-subtle)' }}>Existing record</span>
          </div>
          <PatientPicker
            value={patientLink && patientLink.kind === 'existing' ? patientLink.patient_id : null}
            patientSnapshot={patientSnapshot}
            onSelect={onSelectPatient}
            onCreateNew={onCreateNewPatient}
          />
        </div>

        {/* Create new from extracted block */}
        <div
          role="button"
          tabIndex={0}
          onClick={onCreateNewPatient}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onCreateNewPatient(); }}
          style={{
            border: `1.5px solid ${patientLink && patientLink.kind === 'new' ? 'var(--ab-fg)' : 'var(--ab-border-hair)'}`,
            borderRadius: 'var(--ab-radius-md)',
            padding: 16,
            background: 'var(--ab-bg)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 14,
          }}
        >
          <span
            style={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              border: '1.5px solid var(--ab-fg)',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
              marginTop: 2,
            }}
          >
            {patientLink && patientLink.kind === 'new' && (
              <span
                style={{ width: 11, height: 11, borderRadius: '50%', background: 'var(--ab-fg)' }}
              />
            )}
          </span>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 12,
              }}
            >
              <Icon name="user-plus" size={16} />
              Create a new patient from the extracted block
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 10,
              }}
            >
              {[
                ['Name', `${patCandidate.first_name} ${patCandidate.last_name}`.trim()],
                ['DOB', patCandidate.date_of_birth],
                ['Sex', patCandidate.gender],
                ['Address', patCandidate.address_line1],
                ['City/State', [patCandidate.city, patCandidate.state].filter(Boolean).join(', ')],
                ['Member ID', patCandidate.member_id],
              ].map(([k, v]) => (
                <div key={k}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 800,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: 'var(--ab-fg-subtle)',
                    }}
                  >
                    {k}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, marginTop: 2 }}>{v || '—'}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* ── Provider resolve ── */}
      <Section icon="briefcase" title="Resolve the billing provider">
        <p style={{ margin: '4px 0 16px', fontSize: 14, color: 'var(--ab-fg-subtle)' }}>
          The form named the billing &amp; rendering provider. Link to a provider already in AppealBot, or save a new one for reuse.
        </p>

        {/* Existing provider options */}
        {providers.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
            {providers.map((p) => {
              const isSelected = selectedExistingProviderId === p.id;
              const provName =
                p.organization_name ||
                `${p.first_name || ''} ${p.last_name || ''}`.trim() ||
                p.npi ||
                p.id;
              const provFillsTin = p.tax_id && !bpCandidate.tax_id;
              return (
                <div
                  key={p.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onProviderChange({ kind: 'existing', profile_id: p.id })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ')
                      onProviderChange({ kind: 'existing', profile_id: p.id });
                  }}
                  style={{
                    border: `1.5px solid ${isSelected ? 'var(--ab-fg)' : 'var(--ab-border-hair)'}`,
                    borderRadius: 'var(--ab-radius-md)',
                    padding: 16,
                    background: 'var(--ab-bg)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 14,
                  }}
                >
                  <span
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      border: '1.5px solid var(--ab-fg)',
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                      marginTop: 2,
                    }}
                  >
                    {isSelected && (
                      <span
                        style={{
                          width: 11,
                          height: 11,
                          borderRadius: '50%',
                          background: 'var(--ab-fg)',
                        }}
                      />
                    )}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 15, fontWeight: 700 }}>{provName}</span>
                      <Chip tone="approved" icon="git-merge">
                        Match found
                      </Chip>
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: 'var(--ab-fg-subtle)',
                        display: 'flex',
                        gap: 14,
                        marginTop: 3,
                        flexWrap: 'wrap',
                      }}
                    >
                      {p.npi && <span>NPI {p.npi}</span>}
                      {p.tax_id && <span>TIN {p.tax_id}</span>}
                    </div>
                    {provFillsTin && (
                      <div
                        style={{
                          fontSize: 12,
                          color: 'var(--ab-approved)',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          marginTop: 8,
                        }}
                      >
                        <Icon name="wand" size={13} />
                        Linking fills the missing Box 25 Tax ID ({p.tax_id}) from this record
                      </div>
                    )}
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--ab-fg-subtle)', flexShrink: 0 }}>
                    Existing record
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Create new from extracted block */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => onProviderChange({ kind: 'new' })}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') onProviderChange({ kind: 'new' });
          }}
          style={{
            border: `1.5px solid ${providerLink && providerLink.kind === 'new' ? 'var(--ab-fg)' : 'var(--ab-border-hair)'}`,
            borderRadius: 'var(--ab-radius-md)',
            padding: 16,
            background: 'var(--ab-bg)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 14,
          }}
        >
          <span
            style={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              border: '1.5px solid var(--ab-fg)',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
              marginTop: 2,
            }}
          >
            {providerLink && providerLink.kind === 'new' && (
              <span
                style={{ width: 11, height: 11, borderRadius: '50%', background: 'var(--ab-fg)' }}
              />
            )}
          </span>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 12,
              }}
            >
              <Icon name="plus" size={16} />
              Save a new provider from the extracted block
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 10,
              }}
            >
              {[
                ['Practice', bpCandidate.name],
                ['Billing NPI', bpCandidate.npi],
                ['Tax ID (TIN)', bpCandidate.tax_id],
                ['Address', bpCandidate.address],
                ['Rendering', bpCandidate.rendering],
                ['Rendering NPI', bpCandidate.rendering_npi],
              ].map(([k, v]) => (
                <div key={k}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 800,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: 'var(--ab-fg-subtle)',
                    }}
                  >
                    {k}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      marginTop: 2,
                      color: v ? 'var(--ab-fg)' : 'var(--ab-fg-subtle)',
                    }}
                  >
                    {v || '—'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* TIN fill hint when an existing provider is selected */}
        {fillsTin && (
          <div
            style={{
              fontSize: 12,
              color: 'var(--ab-approved)',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginTop: 12,
            }}
          >
            <Icon name="wand" size={13} />
            Linking fills the missing Box 25 Tax ID ({selectedProvider.tax_id}) from this record
          </div>
        )}
      </Section>
    </div>
  );
}
